import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { trackLogin, trackSignUp, setMetaUser } from '../utils/analytics';

const API = `${window.location.origin}/api`;
const AuthContext = createContext();

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = not auth, object = auth
  const [loading, setLoading] = useState(true);

  // Keep Meta Pixel Advanced Matching + CAPI user_data in sync with auth state.
  // This dramatically boosts Meta's Event Match Quality (EMQ) score which
  // powers ad-optimisation accuracy on iOS/blocked-cookie users.
  useEffect(() => {
    if (user && typeof user === 'object') setMetaUser(user);
    else setMetaUser(null);
  }, [user]);

  const checkAuth = useCallback(async () => {
    try {
      const token = localStorage.getItem('auth_token');
      if (!token) { setUser(false); setLoading(false); return; }
      const res = await axios.get(`${API}/auth/me`, { headers: { Authorization: `Bearer ${token}` } });
      setUser(res.data);
    } catch {
      setUser(false);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = useCallback(async (email, password) => {
    const res = await axios.post(`${API}/auth/login`, { email, password });
    localStorage.setItem('auth_token', res.data.token);
    setUser(res.data);
    trackLogin('email');
    return res.data;
  }, []);

  const register = useCallback(async (name, email, password, phone) => {
    const res = await axios.post(`${API}/auth/register`, { name, email, password, phone });
    localStorage.setItem('auth_token', res.data.token);
    setUser(res.data);
    trackSignUp('email');
    return res.data;
  }, []);

  const loginWithGoogle = useCallback((token, userData) => {
    localStorage.setItem('auth_token', token);
    setUser(userData);
    trackLogin('google');
  }, []);

  const logout = useCallback(async () => {
    try {
      await axios.post(`${API}/auth/logout`, {});
    } catch {}
    localStorage.removeItem('auth_token');
    localStorage.removeItem('admin_token');
    setUser(false);
  }, []);

  const refreshUser = useCallback(async () => {
    try {
      const token = localStorage.getItem('auth_token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const res = await axios.get(`${API}/auth/me`, { headers });
      setUser(res.data);
    } catch {
      setUser(false);
    }
  }, []);

  return (
    <AuthContext.Provider value={{ user, loading, login, register, loginWithGoogle, logout, refreshUser, isAuthenticated: !!user && user !== false }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
